<template lang="pug">
  yz-dialog(v-model="showDialog" persistent scrollable max-width="600")
    yz-card
      yz-dialog-normal-header(:title="$t('panel.add_document')" @close="closeDialog")
      .px-6.py-4
        // Upload photo section
        .mb-4
          .yz-medium-body-xs.text-neutral-700.mb-4 {{ $t('panel.upload_photo') }}
          yz-upload-card(v-model="uploadedPhotos" multiple, append-files, accept="image/*" :primary-title="$t('panel.click_to_upload')" :secondary-title="$t('panel.or_drag_and_drop')" :description="$t('panel.jpg_png_max_5mb')" upload-mode="immediate" @upload-batch-complete="onUploadBatchCompleted")

        // QR Code section
        .d-flex.align-center.gap-4
          yz-upload-qr-databin(:qr-size="100" @databin-created="onQrCreated" @error="onQrError")
          .flex-grow-1
            .yz-medium-body-xs.text-neutral-700.mb-1 {{ $t('panel.add_from_other_devices') }}
            .yz-regular-body-xs.text-neutral-700 {{ $t('panel.scan_qr_instruction') }}

        // Databin synchronization component (hidden)
        yz-databin-sync(v-if="databinKey" v-model="allSyncedFiles" :added-items.sync="uploadedPhotos" :databin-key="databinKey" @error="onDatabinError" ref="databinSync")

        // Preview files
        yz-file-preview.mt-4(v-model="allSyncedFiles" :to-delete-items.sync="fileToDelete" @deleted="onDeletedFiles")

      yz-dialog-normal-actions(:save-label="$t('confirm')" :cancel-label="$t('cancel')" :loading="isLoading" :disable-save="isSaveDisabled" @save="onConfirm" @cancel="closeDialog")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentClaim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Databin key for QRcode upload
      databinKey: null,
      allSyncedFiles: [],
      uploadedPhotos: [],
      fileToDelete: [],
      isLoading: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    isSaveDisabled() {
      // Disable if no files
      if (this.allSyncedFiles.length === 0) return true

      // Disable if any file is still uploading
      return this.allSyncedFiles.some(file => file.uploading === true)
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    closeDialog() {
      // Clean up synced files and files marked for deletion
      const filesToDelete = []

      // Add all synced files
      if (this.allSyncedFiles.length > 0) {
        const syncedIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...syncedIds)
      }

      // Add files marked for deletion
      if (this.fileToDelete.length > 0) {
        const deleteIds = this.fileToDelete.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...deleteIds)
      }

      // Delete all files if any exist
      if (filesToDelete.length > 0) {
        // Fire and forget - delete files asynchronously without blocking dialog close
        this.$axios.post('/documents/bulk_delete', { documents: filesToDelete }).catch(error => {
          console.error('Error deleting files:', error)
        })
      }

      this.showDialog = false
    },
    async onConfirm() {
      // Build documents array from synced files
      const documents = this.allSyncedFiles.length > 0
        ? this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        : []

      // Validate that we have documents to upload
      if (documents.length === 0) {
        await this.showMessage(['error', this.$t('panel.upload_at_least_one_document')])
        return
      }

      this.isLoading = true

      try {
        // Call PATCH endpoint to add documents to claim
        await this.$axios.patch(
          `/panels/${this.company.slug}/claims/${this.parentClaim.id}`,
          { documents }
        )

        await this.showMessage(['success', this.$t('panel.documents_added')])

        // Clean up files marked for deletion after successful upload
        if (this.fileToDelete.length > 0) {
          const deleteIds = this.fileToDelete.map(file => file.hid).filter(Boolean)
          if (deleteIds.length > 0) {
            // Fire and forget - delete files asynchronously
            this.$axios.post('/documents/bulk_delete', { documents: deleteIds }).catch(error => {
              console.error('Error deleting files:', error)
            })
          }
        }

        // Emit event to parent to refresh claim data
        this.$emit('document-added')

        this.showDialog = false
      } catch (error) {
        console.error('Error adding documents to claim:', error)
        await this.showMessage(['error', this.$t('panel.failed_add_documents')])
      } finally {
        this.isLoading = false
      }
    },
    onQrCreated(keyData) {
      this.databinKey = keyData.key
    },
    onQrError(error) {
      console.error('QR Code Error:', error)
      this.showMessage(['error', this.$t('panel.qr_code_error')])
    },
    onDatabinError(error) {
      console.error('Databin Sync Error:', error)
      this.showMessage(['error', this.$t('panel.syncing_files_error')])
    },
    onUploadBatchCompleted(uploadResults) {
      // When files are uploaded, add them to the databin sync
      if (this.$refs.databinSync && uploadResults.length > 0) {
        this.$refs.databinSync.addItems(uploadResults)
      }
    },
    onDeletedFiles(deletedFiles) {
      // When files are deleted from preview, remove them from databin sync
      if (this.$refs.databinSync && deletedFiles.length > 0) {
        this.$refs.databinSync.removeItems(deletedFiles)
      }
    }
  }
}
</script>

<style scoped></style>
